import { useEffect, useRef } from 'react';

/** Max collapsed sheet height as a share of the screen — must match `SmBottomSheet-paper` maxHeight. */
const COLLAPSED_RATIO = 0.8;
const SNAP_MS = 250;
/** iOS sheet curve — fast start, long soft settle. Shared with the open/close slide. */
export const SHEET_EASING = 'cubic-bezier(0.32, 0.72, 0, 1)';
/** Finger travel before deciding whether the gesture moves the sheet or scrolls its content. */
const DECIDE_PX = 6;
/** px/ms — a quick flick counts even when the travel is short. */
const FLING_VELOCITY = 0.5;

type Mode = 'expand' | 'drag' | 'native';

type Gesture = {
  startY: number;
  lastY: number;
  lastTime: number;
  velocity: number;
  startHeight: number;
  canExpand: boolean;
  scroller: HTMLElement | null;
  mode: Mode | null;
};

type UseSheetDragOptions = {
  paper: HTMLDivElement | null;
  open: boolean;
  expanded: boolean;
  onExpandedChange: (expanded: boolean) => void;
  onDismiss: () => void;
};

/** Nearest ancestor of `target` (up to `root`) that can actually scroll vertically. */
function findScroller(target: EventTarget | null, root: HTMLElement): HTMLElement | null {
  let el = target instanceof HTMLElement ? target : null;
  while (el && el !== root) {
    const { overflowY } = getComputedStyle(el);
    if ((overflowY === 'auto' || overflowY === 'scroll') && el.scrollHeight > el.clientHeight) {
      return el;
    }
    el = el.parentElement;
  }
  return null;
}

/**
 * Touch gestures for the mobile bottom sheet:
 * - swipe up on the collapsed sheet (content height, up to 80%) → expand to full screen
 * - swipe down while the content is scrolled to the top → collapse (from full) or close
 * Anything else falls through to native scrolling of the sheet content.
 */
export function useSheetDrag({ paper, open, expanded, onExpandedChange, onDismiss }: UseSheetDragOptions) {
  // Listeners are bound once per open; refs keep them reading the latest props.
  const latest = useRef({ open, expanded, onExpandedChange, onDismiss });
  latest.current = { open, expanded, onExpandedChange, onDismiss };

  useEffect(() => {
    if (!open || !paper) return;

    let gesture: Gesture | null = null;
    let clearTimer: ReturnType<typeof setTimeout> | undefined;
    // Height before expanding — collapsing returns here, since short sheets sit below the 80% cap.
    let collapsedHeight: number | null = null;

    const viewportHeight = () => paper.parentElement?.clientHeight ?? window.innerHeight;

    const setInline = (styles: Partial<Record<'height' | 'maxHeight' | 'transform' | 'transition', string>>) => {
      Object.assign(paper.style, styles);
    };

    /** Animate to a resting state, then hand control back to the class-based styles. */
    const snap = (styles: Parameters<typeof setInline>[0]) => {
      setInline({ transition: `height ${SNAP_MS}ms ${SHEET_EASING}, transform ${SNAP_MS}ms ${SHEET_EASING}`, ...styles });
      clearTimeout(clearTimer);
      clearTimer = setTimeout(() => setInline({ height: '', maxHeight: '', transform: '', transition: '' }), SNAP_MS);
    };

    const onTouchStart = (event: TouchEvent) => {
      if (event.touches.length !== 1) return;
      const y = event.touches[0].clientY;
      const startHeight = paper.getBoundingClientRect().height;
      gesture = {
        startY: y,
        lastY: y,
        lastTime: event.timeStamp,
        velocity: 0,
        startHeight,
        canExpand: !latest.current.expanded,
        scroller: findScroller(event.target, paper),
        mode: null
      };
    };

    const onTouchMove = (event: TouchEvent) => {
      if (!gesture) return;
      const y = event.touches[0].clientY;

      if (!gesture.mode) {
        const dy = y - gesture.startY;
        if (Math.abs(dy) < DECIDE_PX) return;
        if (event.defaultPrevented) {
          // Content that handles its own touch (map, slider, canvas) already claimed this gesture.
          gesture.mode = 'native';
        } else if (dy < 0) {
          gesture.mode = gesture.canExpand ? 'expand' : 'native';
        } else {
          gesture.mode = gesture.scroller && gesture.scroller.scrollTop > 0 ? 'native' : 'drag';
        }
        // Measure from here so the sheet doesn't jump by the decision threshold.
        gesture.startY = y;
        if (gesture.mode !== 'native') setInline({ transition: 'none' });
      }
      if (gesture.mode === 'native') return;

      event.preventDefault();
      const elapsed = event.timeStamp - gesture.lastTime;
      if (elapsed > 0) gesture.velocity = (y - gesture.lastY) / elapsed;
      gesture.lastY = y;
      gesture.lastTime = event.timeStamp;

      const dy = y - gesture.startY;
      if (gesture.mode === 'expand') {
        const height = Math.min(gesture.startHeight - dy, viewportHeight());
        setInline({ maxHeight: '100%', height: `${Math.max(height, gesture.startHeight)}px` });
      } else {
        setInline({ transform: `translateY(${Math.max(dy, 0)}px)` });
      }
    };

    const onTouchEnd = () => {
      if (!gesture || !gesture.mode || gesture.mode === 'native') {
        gesture = null;
        return;
      }
      const { mode, velocity, startY, lastY, startHeight } = gesture;
      gesture = null;
      const dy = lastY - startY;
      const vh = viewportHeight();
      const { expanded: isExpanded, onExpandedChange: setExpanded, onDismiss: dismiss } = latest.current;

      if (mode === 'expand') {
        if (-dy > vh * 0.1 || velocity < -FLING_VELOCITY) {
          collapsedHeight = startHeight;
          snap({ height: `${vh}px` });
          setExpanded(true);
        } else {
          snap({ height: `${startHeight}px` });
        }
        return;
      }

      const flungDown = velocity > FLING_VELOCITY;
      if (isExpanded) {
        if (dy > vh * 0.6 || (flungDown && dy > vh * 0.2)) {
          dismissOrRestore(dismiss);
        } else if (dy > vh * 0.15 || flungDown) {
          snap({ height: `${collapsedHeight ?? vh * COLLAPSED_RATIO}px`, transform: 'translateY(0)' });
          setExpanded(false);
        } else {
          snap({ transform: 'translateY(0)' });
        }
        return;
      }

      if (dy > startHeight * 0.25 || flungDown) {
        dismissOrRestore(dismiss);
      } else {
        snap({ transform: 'translateY(0)' });
      }
    };

    /** Ask the owner to close; if it keeps the dialog open (e.g. blocks dismissal), slide back up. */
    const dismissOrRestore = (dismiss: () => void) => {
      dismiss();
      requestAnimationFrame(() => {
        if (latest.current.open) snap({ transform: 'translateY(0)' });
      });
    };

    paper.addEventListener('touchstart', onTouchStart, { passive: true });
    // Non-passive so a sheet drag can stop the content / page from scrolling underneath.
    paper.addEventListener('touchmove', onTouchMove, { passive: false });
    paper.addEventListener('touchend', onTouchEnd);
    paper.addEventListener('touchcancel', onTouchEnd);
    return () => {
      clearTimeout(clearTimer);
      paper.removeEventListener('touchstart', onTouchStart);
      paper.removeEventListener('touchmove', onTouchMove);
      paper.removeEventListener('touchend', onTouchEnd);
      paper.removeEventListener('touchcancel', onTouchEnd);
    };
  }, [open, paper]);
}
